/* eslint-disable formatjs/no-literal-string-in-jsx */

import React from 'react';
import { A, H2 } from '@osmosis/tailwind-ui/components/Text';
import { text0 } from '@osmosis/tailwind-ui/styles/text';
import { FormattedMessage } from 'react-intl';
import { Box, Flex } from '@osmosis/tailwind-ui/components/Box';
import { Deal, Plan } from '../../types/plans';
import {
  getCurrencySymbol,
  getRenewalTitle,
  trackPlanSelection,
  formatPrice,
  getPlanHref,
} from './utils';
import { cn } from '@osmosis/tailwind-ui/helpers/classes';

type Props = {
  plan: Plan;
  upsellPlan?: Plan | null;
  dealCode?: string;
  deal?: Deal;
  discountPill?: string;
};

type SelectionSavingsProps = {
  amount: number | null;
  text?: string;
};

const SelectionSavings = ({ amount, text }: SelectionSavingsProps) => {
  const pillText = text?.trim()
    ? text.trim().replaceAll('{amount}', String(amount))
    : undefined;

  return (
    <Box
      className="mb-4 flex min-h-8 items-center justify-center font-bold"
      aria-hidden={!amount}
    >
      {amount ? (
        <Box className="mx-auto rounded-full border border-green-200 bg-green-50 px-3 py-1 text-green-800">
          <p className="text-[.85rem] font-bold">
            {pillText || (
              <FormattedMessage
                id="plancardnew.selection.savings"
                defaultMessage="Save {amount}%"
                values={{ amount }}
              />
            )}
          </p>
        </Box>
      ) : (
        <Box className="invisible h-8">&nbsp;</Box>
      )}
    </Box>
  );
};

/** Floors the real discount so the UI never overstates the API's prices. */
export const getSavingsPercentage = (
  originalAmount?: number,
  currentAmount?: number,
): number | null => {
  if (
    !originalAmount ||
    currentAmount == null ||
    currentAmount >= originalAmount
  ) {
    return null;
  }

  return Math.floor(((originalAmount - currentAmount) / originalAmount) * 100);
};

/**
 * Plan Card New.
 */
export const PlanCardNew = ({
  plan,
  upsellPlan,
  dealCode,
  deal,
  discountPill,
}: Props) => {
  const appliedDeal = dealCode
    ? { ...(deal?.url === dealCode ? deal : {}), url: dealCode }
    : undefined;
  const href = getPlanHref(plan, appliedDeal, upsellPlan);
  const currentAmount = plan.currency.unitAmount;
  const originalAmount = plan.currency.origUnitAmount;
  const savingsPercentage = getSavingsPercentage(originalAmount, currentAmount);
  // Savings are only advertised on the annual and two-year cards: short term
  // plans show no strikethrough and no pill even when a deal prices them below
  // list. The strikethrough keeps its height so every price stays aligned.
  const showSavings = savingsPercentage != null && plan.intervalLength >= 12;
  const currencySymbol = getCurrencySymbol(plan.currency.currency);
  const monthlyAmount = plan.intervalLength
    ? Math.round(currentAmount / plan.intervalLength)
    : currentAmount;

  const onBuyClick = async (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
  ) => {
    event.preventDefault();
    await trackPlanSelection(plan, appliedDeal);
    window.location.href = href;
  };

  return (
    <Flex
      data-testid="plan"
      data-plan-code={plan.code}
      key={plan.hash}
      className="bg-wp-light-blue text-wp-black relative h-full w-full flex-col overflow-hidden rounded-b-[7px]"
    >
      <Box className="flex flex-col items-center text-center">
        <H2
          className="font-national-2 mb-4 mt-6 text-xl font-bold"
          data-testid="plan-title"
        >
          <FormattedMessage
            id="plans.title.months"
            defaultMessage="{intervalLength} {intervalLength, plural, one {month} other {months}}"
            values={{ intervalLength: plan.intervalLength }}
          />
        </H2>
      </Box>
      {/*
        The price block uses plain <p> instead of tailwind-ui's <P>: P's base
        classes include a responsive `sm:text-base`, which overrides any
        unprefixed text size above 640px and shrinks these lines on desktop.
      */}
      <Box data-testid="price-slot-plan" className="text-center">
        <p
          className={cn(
            'font-national-2 text-wp-gray h-8 text-2xl line-through',
            !showSavings && 'invisible',
          )}
          aria-hidden={!showSavings}
          data-testid="plan-currency-base"
        >
          {currencySymbol}
          {formatPrice(Math.round(originalAmount ?? currentAmount))}
        </p>

        <Flex
          className="h-14 items-center justify-center"
          data-testid="period-price"
        >
          <p
            className="font-national-2 text-5xl font-bold"
            data-testid="plan-currency-symbol"
          >
            {currencySymbol}
          </p>
          <p
            className="font-national-2 text-5xl font-bold"
            data-testid="plan-currency-amount"
          >
            {formatPrice(Math.round(currentAmount))}
          </p>
        </Flex>

        <p className="text-wp-gray font-national-2 mt-2 text-sm">
          <FormattedMessage
            id="plancardnew.monthly_price"
            defaultMessage="That's {currencySymbol}{price}/month"
            values={{
              currencySymbol,
              price: formatPrice(monthlyAmount),
            }}
          />
        </p>
      </Box>
      <Box className="mt-auto w-full px-6 pb-4 text-center">
        <SelectionSavings
          amount={showSavings ? savingsPercentage : null}
          text={discountPill}
        />
        <Box className="border-t border-gray-300" data-testid="plan-divider" />
        <Box>
          <A
            href={href}
            onClick={onBuyClick}
            data-testid="plan-buy-button"
            className={cn(
              'bg-wp-action-blue hover:bg-wp-vital-orange mt-7 inline-flex h-11 w-full items-center justify-center rounded-full px-4 text-base font-extrabold no-underline outline-none transition-colors duration-100',
              text0,
            )}
          >
            <FormattedMessage
              id="plans.selection.buy"
              defaultMessage="Buy now"
            />
          </A>
          {!!plan.autoRenew && (
            <p className="text-wp-gray font-national-2 mt-3 text-sm">
              <FormattedMessage {...getRenewalTitle(plan.intervalLength)} />
            </p>
          )}
        </Box>
      </Box>
    </Flex>
  );
};
